import React, { useState, useEffect } from 'react';
import { User, Calendar, MapPin, Briefcase, Heart, X, Check, Eye, Clock, CheckCircle, XCircle, Image as ImageIcon, RefreshCw, Edit2, Save } from 'lucide-react';
import { useApiRequest } from '../../hooks/useApiRequest';
import { API_ENDPOINTS } from '../../lib/api-endpoints';
import { PaginationWrapper } from '../ui/PaginationWrapper';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

interface PendingChange {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    profilePhoto?: string;
  };
  profileId: any;
  changeType: string;
  updatedData: any;
  currentData: any;
  changedFields: Array<{
    field: string;
    oldValue: any;
    newValue: any;
  }>;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  reviewedAt?: string;
  rejectionReason?: string;
  submittedAt: string;
  createdAt: string;
}

const PendingProfileChanges: React.FC = () => {
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [selectedChange, setSelectedChange] = useState<PendingChange | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [stats, setStats] = useState({ pending: 0 });
  const [rejectionReason, setRejectionReason] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [confirmApproveId, setConfirmApproveId] = useState<string | null>(null);

  // Cache data for each tab to avoid reloading
  const [dataCache, setDataCache] = useState<Record<string, PendingChange[]>>({});
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());

  // API hooks
  const { loading, execute } = useApiRequest();
  const { loading: actionLoading, execute: executeAction } = useApiRequest();

  useEffect(() => {
    // Only fetch if this tab hasn't been loaded yet
    if (!loadedTabs.has(filterStatus)) {
      fetchPendingChanges();
    } else {
      // Use cached data
      setPendingChanges(dataCache[filterStatus] || []);
    }
  }, [filterStatus]);

  const fetchPendingChanges = async (forceRefresh = false) => {
    const response = await execute(API_ENDPOINTS.ADMIN.PENDING_PROFILE_CHANGES, {
      params: { status: filterStatus, limit: 100 }
    });

    if (response.success && response.data) {
      const changes = response.data.changes;
      setPendingChanges(changes);
      setStats(response.data.stats);

      // Cache the data for this tab
      setDataCache(prev => ({
        ...prev,
        [filterStatus]: changes
      }));

      // Mark this tab as loaded
      setLoadedTabs(prev => new Set(prev).add(filterStatus));
    }
  };

  const invalidateCache = () => {
    // Clear cache to force refresh on next tab visit
    setDataCache({});
    setLoadedTabs(new Set());
  };

  const confirmApproveAction = async () => {
    if (!confirmApproveId) return;

    const payload = isEditMode && Object.keys(editedData).length > 0
      ? { editedData }
      : {};

    const response = await executeAction(API_ENDPOINTS.ADMIN.APPROVE_PROFILE_CHANGE(confirmApproveId), {
      method: 'POST',
      data: payload
    });

    if (response.success) {
      toast.success('Profile changes approved successfully!');
      setShowDetailModal(false);
      setSelectedChange(null);
      setIsEditMode(false);
      setEditedData({});
      setConfirmApproveId(null);
      // Invalidate cache and refresh current tab
      invalidateCache();
      fetchPendingChanges();
    } else {
      toast.error(response.error || 'Failed to approve changes');
    }
  };

  const handleApprove = (changeId: string) => {
    setConfirmApproveId(changeId);
  };

  const handleReject = async (changeId: string) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    const response = await executeAction(API_ENDPOINTS.ADMIN.REJECT_PROFILE_CHANGE(changeId), {
      method: 'POST',
      data: { reason: rejectionReason }
    });

    if (response.success) {
      toast.success('Profile changes rejected');
      setShowDetailModal(false);
      setSelectedChange(null);
      setRejectionReason('');
      // Invalidate cache and refresh current tab
      invalidateCache();
      fetchPendingChanges();
    } else {
      toast.error(response.error || 'Failed to reject changes');
    }
  };

  const handleEditField = (field: string, value: any) => {
    setEditedData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const renderFieldValue = (field: string, value: any, isEditable = false, onChange?: (val: any) => void) => {
    if (value === null || value === undefined) return 'N/A';

    // Handle photos array
    if (field === 'photos' && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((photo: any, index: number) => {
            const photoUrl = typeof photo === 'string' ? photo : (photo.url || photo);
            return (
              <div key={index} className="relative group">
                <img
                  src={photoUrl}
                  alt={`Photo ${index + 1}`}
                  className="w-20 h-20 object-cover rounded border"
                />
                {photo.isPrimary && (
                  <span className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-1 rounded">
                    Primary
                  </span>
                )}
                {photo.isVerified === false && (
                  <span className="absolute bottom-1 left-1 bg-yellow-500 text-white text-xs px-1 rounded">
                    New
                  </span>
                )}
              </div>
            );
          })}
          <div className="text-xs text-gray-500 mt-1 w-full">
            Total: {value.length} photo(s)
          </div>
        </div>
      );
    }

    // Handle profilePhoto - check for base64, http, or https
    if (field === 'profilePhoto' && typeof value === 'string') {
      if (value.startsWith('data:image') || value.startsWith('http')) {
        return <img src={value} alt="Profile" className="w-20 h-20 object-cover rounded border" />;
      } else {
        return <span className="text-sm">{value}</span>;
      }
    }

    // Editable text fields
    if (isEditable && onChange && typeof value === 'string') {
      return (
        <input
          type="text"
          value={editedData[field] !== undefined ? editedData[field] : value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border rounded px-2 py-1 text-sm"
        />
      );
    }

    // Editable number fields
    if (isEditable && onChange && typeof value === 'number') {
      return (
        <input
          type="number"
          value={editedData[field] !== undefined ? editedData[field] : value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border rounded px-2 py-1 text-sm"
        />
      );
    }

    // Handle arrays (e.g., interests, hobbies, maritalStatus)
    if (Array.isArray(value)) {
      return <span>{value.join(', ')}</span>;
    }

    // Handle objects - display field by field
    if (typeof value === 'object' && value !== null) {
      // Special handling for specific object types

      // Partner Preferences
      if (field === 'partnerPreferences') {
        return (
          <div className="space-y-1 text-sm">
            {value.ageRange && (
              <div>Age: {value.ageRange.min || 'Any'} - {value.ageRange.max || 'Any'} years</div>
            )}
            {value.heightRange && (
              <div>Height: {
                value.heightRange.min
                  ? (() => {
                    const cm = value.heightRange.min;
                    const totalInches = Math.round(cm / 2.54);
                    const feet = Math.floor(totalInches / 12);
                    const inches = totalInches % 12;
                    return `${feet}'${inches}"`;
                  })()
                  : 'Any'
              } - {
                  value.heightRange.max
                    ? (() => {
                      const cm = value.heightRange.max;
                      const totalInches = Math.round(cm / 2.54);
                      const feet = Math.floor(totalInches / 12);
                      const inches = totalInches % 12;
                      return `${feet}'${inches}"`;
                    })()
                    : 'Any'
                }</div>
            )}
            {value.maritalStatus && (
              <div>Marital Status: {Array.isArray(value.maritalStatus) ? value.maritalStatus.join(', ') : value.maritalStatus}</div>
            )}
            {value.religion && <div>Religion: {value.religion}</div>}
            {value.diet && <div>Food Preference: {value.diet}</div>}
            {value.smoking && <div>Smoking: {value.smoking}</div>}
            {value.drinking && <div>Drinking: {value.drinking}</div>}
          </div>
        );
      }

      // Education
      if (field === 'education') {
        return (
          <div className="space-y-1 text-sm">
            {value.level && <div>Level: {value.level}</div>}
            {value.field && <div>Field: {value.field}</div>}
            {value.institution && <div>Institution: {value.institution}</div>}
          </div>
        );
      }

      // Family
      if (field === 'family') {
        return (
          <div className="space-y-1 text-sm">
            {value.fatherName && <div>Father: {value.fatherName}</div>}
            {value.fatherOccupation && <div>Father's Occupation: {value.fatherOccupation}</div>}
            {value.motherName && <div>Mother: {value.motherName}</div>}
            {value.motherOccupation && <div>Mother's Occupation: {value.motherOccupation}</div>}
            {value.siblings && (
              <div>Siblings: {value.siblings.brothers || 0} Brothers, {value.siblings.sisters || 0} Sisters</div>
            )}
            {value.familyType && <div>Family Type: {value.familyType}</div>}
            {value.familyStatus && <div>Family Status: {value.familyStatus}</div>}
            {value.familyValues && <div>Family Values: {value.familyValues}</div>}
          </div>
        );
      }

      // Horoscope
      if (field === 'horoscope') {
        return (
          <div className="space-y-1 text-sm">
            {value.birthTime && <div>Birth Time: {value.birthTime}</div>}
            {value.birthPlace && <div>Birth Place: {value.birthPlace}</div>}
            {value.manglik && <div>Manglik: {value.manglik}</div>}
            {value.raashi && <div>Raashi: {value.raashi}</div>}
            {value.nakshatra && <div>Nakshatra: {value.nakshatra}</div>}
          </div>
        );
      }

      // Generic object - show all key-value pairs
      return (
        <div className="space-y-1 text-sm">
          {Object.entries(value).map(([key, val]) => (
            <div key={key}>
              <span className="text-gray-600">{key}:</span> {Array.isArray(val) ? val.join(', ') : String(val)}
            </div>
          ))}
        </div>
      );
    }

    // Handle height - convert from inches to feet'inches" format
    if (field === 'height' && typeof value === 'number') {
      const feet = Math.floor(value / 12);
      const inches = value % 12;
      return `${feet}'${inches}" (${value} inches)`;
    }

    // Handle dates - normalize to consistent format
    if (field.includes('Date') || field.includes('At')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        // If date parsing fails, return as string
      }
    }

    return String(value);
  };

  const viewDetails = (change: PendingChange) => {
    setSelectedChange(change);
    setShowDetailModal(true);
    setRejectionReason('');
    setIsEditMode(false);
    setEditedData({}); // Reset edited data
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-2">Profile Edit Approvals</h1>
          <p className="text-gray-600">Review and approve profile changes made by users</p>
        </div>
        <button
          onClick={() => {
            invalidateCache();
            fetchPendingChanges();
          }}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-600 text-sm">Pending Approval</p>
              <p className="text-2xl text-yellow-700 mt-1">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2 border-b">
        <button
          onClick={() => setFilterStatus('pending')}
          className={`px-4 py-2 -mb-px ${filterStatus === 'pending'
            ? 'border-b-2 border-pink-600 text-pink-600'
            : 'text-gray-600 hover:text-gray-800'
            }`}
        >
          Pending ({stats.pending})
        </button>
        <button
          onClick={() => setFilterStatus('approved')}
          className={`px-4 py-2 -mb-px ${filterStatus === 'approved'
            ? 'border-b-2 border-pink-600 text-pink-600'
            : 'text-gray-600 hover:text-gray-800'
            }`}
        >
          Approved
        </button>
        <button
          onClick={() => setFilterStatus('rejected')}
          className={`px-4 py-2 -mb-px ${filterStatus === 'rejected'
            ? 'border-b-2 border-pink-600 text-pink-600'
            : 'text-gray-600 hover:text-gray-800'
            }`}
        >
          Rejected
        </button>
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 -mb-px ${filterStatus === 'all'
            ? 'border-b-2 border-pink-600 text-pink-600'
            : 'text-gray-600 hover:text-gray-800'
            }`}
        >
          All
        </button>
      </div>

      {/* Changes List */}
      {pendingChanges.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No {filterStatus !== 'all' ? filterStatus : ''} profile changes found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Changes</th>
                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pendingChanges.map((change) => (
                <tr key={change._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {change.userId?.profilePhoto ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={change.userId.profilePhoto}
                            alt=""
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm">{change.userId?.firstName} {change.userId?.lastName}</div>
                        <div className="text-sm text-gray-500">{change.userId?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {change.changedFields.length} field{change.changedFields.length !== 1 ? 's' : ''} changed
                    </div>
                    <div className="text-xs text-gray-500">
                      {change.changedFields.slice(0, 3).map(f => f.field).join(', ')}
                      {change.changedFields.length > 3 && '...'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(change.submittedAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {change.status === 'pending' && (
                      <span className="px-2 inline-flex text-xs leading-5 rounded-full bg-yellow-100 text-yellow-800">
                        <Clock className="w-3 h-3 mr-1" /> Pending
                      </span>
                    )}
                    {change.status === 'approved' && (
                      <span className="px-2 inline-flex text-xs leading-5 rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" /> Approved
                      </span>
                    )}
                    {change.status === 'rejected' && (
                      <span className="px-2 inline-flex text-xs leading-5 rounded-full bg-red-100 text-red-800">
                        <XCircle className="w-3 h-3 mr-1" /> Rejected
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => viewDetails(change)}
                      className="text-pink-600 hover:text-pink-900 mr-3"
                    >
                      <Eye className="w-5 h-5 inline" /> View
                    </button>
                    {change.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(change._id)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          disabled={actionLoading}
                        >
                          <Check className="w-5 h-5 inline" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedChange(change);
                            setShowDetailModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          disabled={actionLoading}
                        >
                          <X className="w-5 h-5 inline" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-xl">Profile Change Details</h2>
                {selectedChange.status === 'pending' && (
                  <button
                    onClick={() => setIsEditMode(!isEditMode)}
                    className={`flex items-center gap-1 px-3 py-1 rounded text-sm ${isEditMode
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                  >
                    {isEditMode ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                    {isEditMode ? 'Edit Mode On' : 'Enable Edit'}
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedChange(null);
                  setRejectionReason('');
                  setIsEditMode(false);
                  setEditedData({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* User Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  {selectedChange.userId?.profilePhoto ? (
                    <img
                      src={selectedChange.userId.profilePhoto}
                      alt=""
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                      <User className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg">{selectedChange.userId?.firstName} {selectedChange.userId?.lastName}</h3>
                    <p className="text-sm text-gray-600">{selectedChange.userId?.email}</p>
                    {selectedChange.userId?.phone && (
                      <p className="text-sm text-gray-600">{selectedChange.userId.phone}</p>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  Submitted: {new Date(selectedChange.submittedAt).toLocaleString()}
                </div>
                {selectedChange.reviewedAt && (
                  <div className="text-sm text-gray-500 mt-1">
                    Reviewed: {new Date(selectedChange.reviewedAt).toLocaleString()} by {selectedChange.reviewedBy?.firstName} {selectedChange.reviewedBy?.lastName}
                  </div>
                )}
              </div>

              {isEditMode && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Edit Mode:</strong> You can now edit the new values before approving. Modified fields will be saved when you approve.
                  </p>
                </div>
              )}

              {/* Changed Fields */}
              <div className="mb-6">
                <h3 className="text-lg mb-4">Changes ({selectedChange.changedFields.length})</h3>
                <div className="space-y-4">
                  {selectedChange.changedFields.map((field, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-2">Field: <strong>{field.field}</strong></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Current Value:</div>
                          <div className="bg-red-50 p-2 rounded border border-red-200">
                            {renderFieldValue(field.field, field.oldValue)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">
                            New Value: {isEditMode && <span className="text-blue-600">(Editable)</span>}
                          </div>
                          <div className={`p-2 rounded border ${isEditMode ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                            {renderFieldValue(
                              field.field,
                              field.newValue,
                              isEditMode && !['photos', 'profilePhoto'].includes(field.field),
                              (val) => handleEditField(field.field, val)
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rejection Reason (if rejected) */}
              {selectedChange.status === 'rejected' && selectedChange.rejectionReason && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm text-red-800 mb-2">Rejection Reason:</h4>
                  <p className="text-sm text-red-700">{selectedChange.rejectionReason}</p>
                </div>
              )}

              {/* Actions */}
              {selectedChange.status === 'pending' && (
                <div className="border-t pt-6">
                  <div className="mb-4">
                    <label className="block text-sm mb-2">Rejection Reason (optional for rejection):</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full border rounded-lg p-2 text-sm"
                      rows={3}
                      placeholder="Enter reason for rejection..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(selectedChange._id)}
                      disabled={actionLoading}
                      className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Check className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : isEditMode ? 'Approve with Edits' : 'Approve Changes'}
                    </button>
                    <button
                      onClick={() => handleReject(selectedChange._id)}
                      disabled={actionLoading || !rejectionReason.trim()}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <X className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : 'Reject Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmApproveId} onOpenChange={(open) => !open && setConfirmApproveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve these profile changes? This will apply the changes to the user's live profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmApproveAction} disabled={actionLoading}>
              {actionLoading ? 'Approving...' : 'Approve'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PendingProfileChanges;